import { useEffect, useRef, useCallback } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import { getMapTilerStreetTileLayerConfig } from '../../config/mapTiles';

const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

const ZIMBABWE_CENTER: [number, number] = [-19.0154, 29.1549];
const ZIMBABWE_ZOOM = 6;
const PIN_ZOOM = 12;

const BOUNDARY_STYLE: L.PathOptions = {
  color: '#2563eb',
  weight: 2,
  opacity: 0.8,
  fillColor: '#3b82f6',
  fillOpacity: 0.1,
};

const BOUNDARY_FLASH_STYLE: L.PathOptions = {
  color: '#dc2626',
  weight: 3,
  opacity: 1,
  fillColor: '#ef4444',
  fillOpacity: 0.2,
};

/**
 * Ray-casting point-in-polygon check.
 * Works with Polygon and MultiPolygon GeoJSON geometries.
 */
function isPointInGeometry(lat: number, lng: number, geometry: GeoJSON.Geometry): boolean {
  if (geometry.type === 'Polygon') {
    return isPointInPolygon(lat, lng, geometry.coordinates);
  }
  if (geometry.type === 'MultiPolygon') {
    return geometry.coordinates.some(poly => isPointInPolygon(lat, lng, poly));
  }
  return false;
}

function isPointInPolygon(lat: number, lng: number, coordinates: number[][][]): boolean {
  const ring = coordinates[0];
  if (!ring) return false;

  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][1], yi = ring[i][0];
    const xj = ring[j][1], yj = ring[j][0];

    const intersect = ((yi > lng) !== (yj > lng)) &&
      (lat < (xj - xi) * (lng - yi) / (yj - yi) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}

type FarmerLocationMapProps = {
  latitude?: number;
  longitude?: number;
  onCoordinateChange: (lat: number | undefined, lng: number | undefined) => void;
  readOnly?: boolean;
  boundaryGeometry?: GeoJSON.Geometry;
};

export function FarmerLocationMap({ latitude, longitude, onCoordinateChange, readOnly, boundaryGeometry }: FarmerLocationMapProps) {
  const mapRef = useRef<L.Map | null>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const boundaryLayerRef = useRef<L.GeoJSON | null>(null);
  const flashTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const updateMarker = useCallback((lat: number, lng: number) => {
    if (!mapRef.current) return;
    const map = mapRef.current;

    if (markerRef.current) {
      markerRef.current.setLatLng([lat, lng]);
    } else {
      markerRef.current = L.marker([lat, lng], { icon: DefaultIcon }).addTo(map);
    }
  }, []);

  const removeMarker = useCallback(() => {
    if (markerRef.current && mapRef.current) {
      mapRef.current.removeLayer(markerRef.current);
      markerRef.current = null;
    }
  }, []);

  // Initialize map
  useEffect(() => {
    if (!mapContainerRef.current) return;

    const hasCoords = latitude != null && longitude != null && !isNaN(latitude) && !isNaN(longitude);
    const center: [number, number] = hasCoords ? [latitude, longitude] : ZIMBABWE_CENTER;
    const zoom = hasCoords ? PIN_ZOOM : ZIMBABWE_ZOOM;

    mapRef.current = L.map(mapContainerRef.current).setView(center, zoom);

    const tiles = getMapTilerStreetTileLayerConfig();
    L.tileLayer(tiles.url, {
      attribution: tiles.attribution,
      maxZoom: tiles.maxZoom,
    }).addTo(mapRef.current);

    if (hasCoords) {
      updateMarker(latitude, longitude);
    }

    return () => {
      if (flashTimeoutRef.current) clearTimeout(flashTimeoutRef.current);
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        markerRef.current = null;
        boundaryLayerRef.current = null;
      }
    };
  }, []);

  // Sync marker position when lat/lng form fields change
  useEffect(() => {
    if (!mapRef.current) return;

    const hasCoords = latitude != null && longitude != null && !isNaN(latitude) && !isNaN(longitude);

    if (hasCoords) {
      updateMarker(latitude, longitude);
    } else {
      removeMarker();
    }
  }, [latitude, longitude, updateMarker, removeMarker]);

  // Render boundary polygon
  useEffect(() => {
    if (!mapRef.current) return;
    const map = mapRef.current;

    if (boundaryLayerRef.current) {
      map.removeLayer(boundaryLayerRef.current);
      boundaryLayerRef.current = null;
    }

    if (!boundaryGeometry) return;

    const geojsonFeature: GeoJSON.Feature = {
      type: 'Feature',
      properties: {},
      geometry: boundaryGeometry,
    };

    boundaryLayerRef.current = L.geoJSON(geojsonFeature, { style: BOUNDARY_STYLE }).addTo(map);

    const bounds = boundaryLayerRef.current.getBounds();
    if (bounds.isValid()) {
      const hasMarker = latitude != null && longitude != null && !isNaN(latitude) && !isNaN(longitude);
      if (hasMarker) {
        bounds.extend([latitude, longitude]);
      }
      map.fitBounds(bounds, { padding: [30, 30] });
    }
  }, [boundaryGeometry]);

  // Handle map click: validate against boundary and update coordinates
  useEffect(() => {
    if (!mapRef.current || readOnly) return;
    const map = mapRef.current;

    const handleClick = (e: L.LeafletMouseEvent) => {
      const { lat, lng } = e.latlng;
      const roundedLat = Math.round(lat * 1000000) / 1000000;
      const roundedLng = Math.round(lng * 1000000) / 1000000;

      if (boundaryGeometry && !isPointInGeometry(roundedLat, roundedLng, boundaryGeometry)) {
        if (boundaryLayerRef.current) {
          boundaryLayerRef.current.setStyle(BOUNDARY_FLASH_STYLE);
          if (flashTimeoutRef.current) clearTimeout(flashTimeoutRef.current);
          flashTimeoutRef.current = setTimeout(() => {
            boundaryLayerRef.current?.setStyle(BOUNDARY_STYLE);
          }, 600);
        }
        return;
      }

      onCoordinateChange(roundedLat, roundedLng);
    };

    map.on('click', handleClick);
    return () => {
      map.off('click', handleClick);
    };
  }, [onCoordinateChange, readOnly, boundaryGeometry]);

  return (
    <div
      ref={mapContainerRef}
      style={{ height: '300px', minHeight: '300px' }}
      className={`rounded-lg border border-gray-200 ${readOnly ? '' : 'cursor-crosshair'}`}
    />
  );
}
